import { useState } from 'react'
import PropTypes from 'prop-types'
import { apiFetch } from '../utils/api'
import { validatePasswordComplexity } from '../utils/validation'

const BETA_FALLBACK_MESSAGE = 'Beta access could not be activated.'

// Map the backend's stable beta_error_code to the English UI text.
const BETA_ERROR_MESSAGES = {
  invalid_plan: 'Invalid beta plan.',
  ended: 'The beta phase has ended.',
  revoked: 'Your beta access has been deactivated.',
  internal: 'Beta access could not be assigned.',
  limit_reached: 'The beta is fully booked. Your account was created, but no beta license could be assigned.'
}

const Register = ({ onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    street: '',
    postalCode: '',
    city: '',
    email: '',
    password: '',
    confirmPassword: '',
    planId: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [betaWarning, setBetaWarning] = useState('')

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (error) setError('')
  }

  const validateForm = () => {
    const requiredFields = [
      ['firstName', 'First name'],
      ['lastName', 'Last name'],
      ['street', 'Street'],
      ['postalCode', 'Postal code'],
      ['city', 'City'],
      ['email', 'Email'],
    ]
    for (const [field, label] of requiredFields) {
      if (!formData[field].trim()) {
        setError(`${label} is required`)
        return false
      }
    }
    const passwordError = validatePasswordComplexity(formData.password)
    if (passwordError) {
      setError(passwordError)
      return false
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return false
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setError('')
    setSuccess('')
    setBetaWarning('')

    try {
      const response = await apiFetch('/api/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          street: formData.street,
          postalCode: formData.postalCode,
          city: formData.city,
          email: formData.email,
          password: formData.password,
          planId: formData.planId.trim()
        }),
      })

      const data = await response.json()

      if (response.ok) {
        let message = 'Registration successful! Please check your emails for the verification link.'
        if (data.beta_status === 'pending') {
          // The beta license is created when the email address is verified.
          message += ' Your beta access will be activated once you have verified your email address.'
        } else if (data.beta_status === 'failed') {
          setBetaWarning(
            Object.hasOwn(BETA_ERROR_MESSAGES, data.beta_error_code)
              ? BETA_ERROR_MESSAGES[data.beta_error_code]
              : BETA_FALLBACK_MESSAGE
          )
        }
        setSuccess(message)
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          street: '',
          postalCode: '',
          city: '',
          email: '',
          password: '',
          confirmPassword: '',
          planId: ''
        })
      } else {
        setError(data.error || 'Registration failed')
      }
    } catch (err) {
      setError('Network error. Please check if the backend server is running.')
      console.error('Registration error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="login-container">
      {/* Logo */}
      <div className="logo">
        <h1>CodeNexus</h1>
        <p>Manage your licenses with ease</p>
      </div>

      {/* Register Title */}
      <div className="login-title">
        <h2>Registration</h2>
        <p>Create your new account</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      {/* Beta Warning (registration succeeded, beta assignment failed) */}
      {betaWarning && (
        <output
          className="error-message"
          style={{
            display: 'block',
            background: 'rgba(120, 53, 15, 0.85)',
            borderColor: 'rgba(245, 158, 11, 0.4)',
            color: '#fffbeb'
          }}
        >
          {betaWarning}
        </output>
      )}

      {/* Register Form */}
      <form className="login-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Your First Name"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Your Last Name"
              required
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="street">Street</label>
          <input
            type="text"
            id="street"
            name="street"
            value={formData.street}
            onChange={handleChange}
            placeholder="Street and House Number"
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="postalCode">Postal Code</label>
            <input
              type="text"
              id="postalCode"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              placeholder="Postal Code"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="city">City</label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Your City"
              required
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your.email@example.com"
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Min 12 chars, uppercase, lowercase, digit, special char"
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Repeat Password"
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="planId">Beta Plan ID (optional)</label>
          <input
            type="text"
            id="planId"
            name="planId"
            value={formData.planId}
            onChange={handleChange}
            placeholder="Beta Plan ID (optional)"
            disabled={isLoading}
          />
        </div>

        <button 
          type="submit" 
          className="login-button"
          disabled={isLoading}
        >
          {isLoading && <span className="loading-spinner"></span>}
          {isLoading ? 'Registration is loading...' : 'Register'}
        </button>
      </form>

      {/* Switch to Login */}
      <div style={{ 
        marginTop: '24px', 
        textAlign: 'center', 
        fontSize: '0.875rem',
        color: '#9ca3af'
      }}>
        <p>
          Already have an account?{' '}
          <button 
            type="button"
            onClick={onSwitchToLogin}
            style={{ 
              color: '#4f46e5', 
              background: 'none',
              border: 'none',
              textDecoration: 'none',
              fontWeight: '500',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            onFocus={(e) => e.target.style.textDecoration = 'underline'}
            onBlur={(e) => e.target.style.textDecoration = 'none'}
          >
            Sign in here
          </button>
        </p>
      </div>
    </div>
  )
}

Register.propTypes = {
  onSwitchToLogin: PropTypes.func.isRequired
}

export default Register 